import { createSlice, PayloadAction, createAsyncThunk, nanoid } from "@reduxjs/toolkit";

// fnPullSessionLogs removed from atollapi.ts (Sputnik-specific, unused in Atoll)
const fnPullSessionLogs = async (...args: any[]) => { console.warn('fnPullSessionLogs removed — Sputnik legacy'); return { data: [] }; };

export enum Severity {
  Emergency = 0,
  Critical = 1,
  Alert = 2,
  Error = 3,
  Warning = 4,
  Debug = 5,
  Informational = 6,
  Notice = 7,
}
interface Position {
  x: number;
  y: number;
}
interface LogEntry {
  id: string; // Add an "id" field for the unique ID
  message: string;
  severity: Severity;
}

interface ConsoleState {
  logs: LogEntry[];
  severityCount: Record<Severity, number>; // Track count of each severity
  mostSevere: Severity | null; // Track the maximum severity logged
  loading: 'idle' | 'loading';
  consoleOpen: boolean;
  startPosition: Position | null
}
const MAX_LOGS = 100; // Maximum number of log entries to keep
const initialState: ConsoleState = {
  logs: [],
  severityCount: {
    [Severity.Emergency]: 0,
    [Severity.Critical]: 0,
    [Severity.Alert]: 0,
    [Severity.Error]: 0,
    [Severity.Warning]: 0,
    [Severity.Debug]: 0,
    [Severity.Informational]: 0,
    [Severity.Notice]: 0,
  },
  mostSevere: null,
  loading: 'idle' ,
  consoleOpen: false,
  startPosition:null
};

export const severityIcons: Record<Severity, string> = {
  [Severity.Emergency]: "Error",
  [Severity.Critical]: "Warning",
  [Severity.Alert]: "Info",
  [Severity.Error]: "Help",
  [Severity.Warning]: "PriorityHigh",
  [Severity.Debug]: "CheckCircle",
  [Severity.Informational]: "Announcement",
  [Severity.Notice]: "Cancel"
};
export const severityColors: Record<Severity, string> = {
  [Severity.Emergency]: "red",
  [Severity.Critical]: "orangered",
  [Severity.Alert]: "darkorange",
  [Severity.Error]: "orange",
  [Severity.Warning]: "gold",
  [Severity.Debug]: "yellowgreen",
  [Severity.Informational]: "limegreen",
  [Severity.Notice]: "green"
};
interface AddLogPayload {
  message: string;
  severity: Severity;
}

export const fetchSessionLogs = createAsyncThunk(
  "console/fetchSessionLogs",
  async ({ lastID, pageLen }: { lastID: number; pageLen: number }, thunkAPI) => {
    const response = await fnPullSessionLogs(lastID, pageLen, thunkAPI.dispatch);
    return response.data;
  }
);

export const consoleSlice = createSlice({
  name: "console",
  initialState,
  reducers: {
    addLog: (state, action: PayloadAction<AddLogPayload>) => {
      const { severity, message } = action.payload;
      const id = nanoid(); // Generate a unique ID using nanoid
      // If we've reached max logs, remove the oldest log (first in array)
      if (state.logs.length >= MAX_LOGS) {
        state.logs.shift();
      }

      // Add the new log
      state.logs.push({ id, message, severity });
      
      // Update severity count
      state.severityCount[severity] += 1;

      // Update maximum severity
      if (state.mostSevere === null || severity < state.mostSevere) {
        state.mostSevere = severity;
      }
    },
    removeLog: (state) => {
      // Remove the oldest log
      if (state.logs.length > 0) {
        const { severity } = state.logs.shift()!;
        
        // Update severity count
        state.severityCount[severity] -= 1;
        if (state.severityCount[severity] <= 0) {
          // Update maximum severity if necessary
          const severities = Object.keys(state.severityCount) as (keyof typeof Severity)[];
          //@ts-ignore
          const nonZeroSeverities = severities.filter((s) => state.severityCount[s] > 0);
          //@ts-ignore
          state.mostSevere = nonZeroSeverities.length > 0 ? Math.min(...nonZeroSeverities) : null;
        };



      }
    },
    setConsoleOpen: (state, action: PayloadAction<{consoleOpen: boolean, position?: Position}>) => {
      state.consoleOpen = action.payload.consoleOpen;
      if (action.payload.position) {state.startPosition = action.payload.position;}
    }
    

  },
  extraReducers: (builder) => {
    builder
      // ...
      .addCase(fetchSessionLogs.pending, (state) => {
        // Set the loading state
        state.loading = "loading";
      })
      .addCase(fetchSessionLogs.fulfilled, (state, action) => {
        // Update the state with the fetched logs
        state.loading = "idle";
        state.logs.push(...action.payload);
      })
      .addCase(fetchSessionLogs.rejected, (state) => {
        // Handle the rejected state if necessary
        state.loading = "idle";
      });
  },
});


export const { addLog, removeLog, setConsoleOpen } = consoleSlice.actions;
export const selectLogs = (state: { console: ConsoleState }) => state.console.logs;
export const selectSeverityCount = (state: { console: ConsoleState }) => state.console.severityCount;
export const selectMostSevere = (state: { console: ConsoleState }) => state.console.mostSevere;
export const selectConsoleOpen = (state: { console: ConsoleState }) => state.console.consoleOpen;
export const selectStartPosition = (state: { console: ConsoleState }) => state.console.startPosition;

export default consoleSlice.reducer;
